// Périodes prédéfinies, paramètres d'API et cycle de chargement par onglet.
import { computed, ref, watch } from '@vue/composition-api'
import { getDateStr } from '@/utils/dates'
import useProxy from '@/hooks/useProxy'
import useAxios from '@/hooks/useAxios'
import { fDayLong } from './ytFormat'

export const API_BASE = '/audience/youtube-perf'

export const PERIOD_ITEMS = [
  { value: '7', text: '7 derniers jours' },
  { value: '28', text: '28 derniers jours' },
  { value: '90', text: '90 derniers jours' },
  { value: '365', text: '12 derniers mois' },
  { value: 'ytd', text: 'Depuis le 1er janvier' },
  { value: 'lm', text: 'Mois dernier' },
  { value: 'custom', text: 'Personnalisée' },
]

export const AGE_ITEMS = [
  { value: 'all', label: 'Toutes les vidéos' },
  { value: 'new', label: 'Publiées sur la période' },
  { value: 'catalog', label: 'Catalogue' },
]

const addDays = (iso, days) => {
  const date = new Date(`${iso}T00:00:00`)
  date.setDate(date.getDate() + days)
  return getDateStr(date)
}

// La borne haute est la dernière donnée disponible (J-3 côté YouTube),
// jamais « aujourd'hui ».
export const computeRange = (period, lastDate, customFrom, customTo) => {
  const end = lastDate || addDays(getDateStr(new Date()), -3)
  const endDate = new Date(`${end}T00:00:00`)
  switch (period) {
    case '7': return { from: addDays(end, -6), to: end }
    case '90': return { from: addDays(end, -89), to: end }
    case '365': return { from: addDays(end, -364), to: end }
    case 'ytd': return { from: `${endDate.getFullYear()}-01-01`, to: end }
    case 'lm': {
      const first = new Date(endDate.getFullYear(), endDate.getMonth() - 1, 1)
      const last = new Date(endDate.getFullYear(), endDate.getMonth(), 0)
      return { from: getDateStr(first), to: getDateStr(last) }
    }
    case 'custom': {
      const to = customTo && customTo <= end ? customTo : end
      const from = customFrom && customFrom <= to ? customFrom : addDays(to, -27)
      return { from, to }
    }
    default: return { from: addDays(end, -27), to: end }
  }
}

export const periodLabel = (period, range) => {
  const preset = PERIOD_ITEMS.find((item) => item.value === period)
  const dates = `du ${fDayLong(range.from)} au ${fDayLong(range.to)}`
  return period === 'custom' || !preset ? dates : `${preset.text} (${dates})`
}

const useYoutubePerf = () => {
  const { store } = useProxy()
  const { axiosGet, isLoading } = useAxios()

  const period = computed(() => store.getters['youtubePerf/getPeriod'])
  const lastDate = computed(() => store.getters['youtubePerf/getLastDate'])
  const range = computed(() => computeRange(
    period.value,
    lastDate.value,
    store.getters['youtubePerf/getCustomFrom'],
    store.getters['youtubePerf/getCustomTo'],
  ))
  const nbDays = computed(() => {
    const from = new Date(`${range.value.from}T00:00:00`)
    const to = new Date(`${range.value.to}T00:00:00`)
    return Math.round((to - from) / 86400000) + 1
  })
  const filtersVersion = computed(() => store.getters['youtubePerf/getFiltersVersion'])
  const params = computed(() => {
    const result = {
      date_from: range.value.from,
      date_to: range.value.to,
      age: store.getters['youtubePerf/getAge'],
    }
    const siteGroupId = store.getters['youtubePerf/getSiteGroupId']
    const siteId = store.getters['youtubePerf/getSiteId']
    if (siteGroupId) result.site_group_id = siteGroupId
    if (siteId) result.site_id = siteId
    return result
  })
  const label = computed(() => periodLabel(period.value, range.value))

  const get = (path, extra = {}) => axiosGet(`${API_BASE}${path}`, { ...params.value, ...extra })

  const ensureOverview = async () => {
    const overview = store.getters['youtubePerf/getOverview']
    if (overview && overview.version === filtersVersion.value) return overview
    const { data } = await get('/overview')
    const stored = { ...data, version: filtersVersion.value }
    store.commit('youtubePerf/setOverview', stored)
    return stored
  }

  // Un onglet charge quand il devient actif et que ses données sont en retard
  // sur les filtres ; il ne recharge pas tant que rien n'a changé.
  const useTabLoader = (active, loader) => {
    const loadedVersion = ref(-1)
    const load = async () => {
      if (!active.value || loadedVersion.value === filtersVersion.value) return
      const version = filtersVersion.value
      await loader()
      loadedVersion.value = version
    }
    watch([active, filtersVersion], load, { immediate: true })
    return { reload: () => { loadedVersion.value = -1; return load() } }
  }

  return { store, axiosGet, isLoading, get, params, range, nbDays, label, period, lastDate, filtersVersion, ensureOverview, useTabLoader }
}

export default useYoutubePerf
